import React from "react";
import { Link } from "react-router-dom";
import MarketingHeader from "@/components/MarketingHeader";

const SECTIONS = [
  ["acceptance", "1. Acceptance of Terms"],
  ["definitions", "2. Definitions"],
  ["service", "3. Description of the Service"],
  ["accounts", "4. Accounts & Authorized Users"],
  ["license", "5. Subscription License & Use Restrictions"],
  ["plans", "6. Plans, Fees & Payment"],
  ["content", "7. Customer Data & Proposal Assets"],
  ["ai", "8. Generated Outputs"],
  ["compliance", "9. Government Compliance & Regulatory Disclaimers"],
  ["samgov", "10. SAM.gov & Third-Party Government Portals"],
  ["acceptable-use", "11. Acceptable Use"],
  ["confidentiality", "12. Confidentiality"],
  ["ip", "13. Intellectual Property"],
  ["security", "14. Security"],
  ["term", "15. Term, Suspension & Termination"],
  ["disclaimers", "16. Disclaimers"],
  ["liability", "17. Limitation of Liability"],
  ["indemnity", "18. Indemnification"],
  ["law", "19. Governing Law, Dispute Resolution & Waivers"],
  ["changes", "20. Changes to These Terms"],
  ["contact", "21. Contact"],
];

export default function Terms() {
  return (
    <div className="mkt-page" data-testid="page-terms">
      <MarketingHeader />

      <section className="mkt-hero">
        <div className="mkt-eyebrow">◢ LEGAL</div>
        <h1 className="mkt-h1">Terms of Service</h1>
        <p className="legal-updated">Last updated: July 23, 2026</p>
      </section>

      <section className="mkt-body">
        <div className="legal-doc">
          <nav className="legal-toc" aria-label="Table of contents">
            <div className="legal-toc-title">On this page</div>
            <ol>
              {SECTIONS.map(([id, label]) => (
                <li key={id}><a href={`#${id}`}>{label}</a></li>
              ))}
            </ol>
          </nav>

          <p>
            These Terms of Service ("<strong>Terms</strong>") govern access to and use of GovCo.AI, an
            AI-assisted platform for discovering, drafting, and managing responses to U.S. government
            solicitations (the "<strong>Service</strong>"), provided by GovCo.AI, LLC, a Virginia
            limited liability company with its principal place of business in Fairfax County,
            Virginia ("<strong>GovCo.AI</strong>," "<strong>we</strong>," "<strong>us</strong>," or
            "<strong>our</strong>"). By creating an account, joining an organization, or otherwise using
            the Service, you agree to these Terms on behalf of yourself and, if applicable, the
            organization you represent ("<strong>Customer</strong>" or "<strong>you</strong>").
          </p>

          <h2 id="acceptance">1. Acceptance of Terms</h2>
          <p>
            You must be at least 18 years old and able to form a binding contract to use the Service.
            If you're using the Service on behalf of a company or other legal entity, you represent that
            you have the authority to bind that entity to these Terms, in which case "you" and
            "Customer" refer to that entity.
          </p>

          <h2 id="definitions">2. Definitions</h2>
          <p>In addition to terms defined elsewhere in these Terms:</p>
          <ul>
            <li>
              "<strong>Org</strong>" means the single organization each account belongs to — the
              customer entity on whose behalf the Service is used.
            </li>
            <li>
              "<strong>Authorized Users</strong>" means the individuals an Org has granted access to the
              Service, in any of its role types (currently Administrator, Proposal Manager, Contributor,
              Reviewer, External Reviewer, KB Manager, and Auditor).
            </li>
            <li>
              "<strong>Customer Data</strong>" means any information, files, or data that Customer or an
              Authorized User uploads, imports, or submits to the Service — including solicitation
              documents, company capability profiles (skills, certifications, NAICS codes, clearances,
              past performance), and pricing information.
            </li>
            <li>
              "<strong>Proposal Assets</strong>" means the proposals, compliance/requirement matrices,
              drafts, comments, version history, color-team snapshots, and export packages created within
              the Service from Customer Data, whether authored manually or with the assistance of
              Generated Outputs.
            </li>
            <li>
              "<strong>Generated Outputs</strong>" means content produced by the Service's AI features
              (including requirement shredding, draft generation, and inline editing assistance) using
              Customer Data as input, as described in <a href="#ai">Section 8</a>.
            </li>
            <li>
              "<strong>Third-Party Government Portals</strong>" means SAM.gov and other U.S. government
              procurement systems and portals (such as eBuy and PIEE) that the Service may reference or
              import data from, or that Customer may use independently to submit a proposal. As of the
              date of these Terms, the Service imports opportunity data only from SAM.gov's public
              Opportunities API and does not integrate with, or submit to, eBuy, PIEE, or any other such
              portal on Customer's behalf.
            </li>
          </ul>

          <h2 id="service">3. Description of the Service</h2>
          <p>
            GovCo.AI helps government contractors discover solicitations (including data sourced from
            SAM.gov), extract and track compliance requirements, draft proposal content with AI
            assistance, collaborate with teammates, and export submission packages. The Service is an
            administrative and workflow tool — it does not submit anything to any Third-Party Government
            Portal or government agency on your behalf, and it is not affiliated with, endorsed by, or
            operated by the U.S. General Services Administration, SAM.gov, or any other government body.
          </p>

          <h2 id="accounts">4. Accounts & Authorized Users</h2>
          <p>Each account belongs to exactly one Org. When you register:</p>
          <ul>
            <li>
              If your work email domain doesn't match an existing Org, you create a new one and become
              its <strong>Administrator</strong>, responsible for managing its Authorized Users, roles,
              and data.
            </li>
            <li>
              If your work email domain matches an existing Org, your request to join is sent to that
              Org's Administrator(s) for approval. You won't have access until approved.
            </li>
            <li>
              You're responsible for maintaining the confidentiality of your login credentials and for
              all activity under your account. Notify us promptly of any unauthorized use.
            </li>
            <li>
              An Org's Administrator can view, manage, deactivate, or remove Authorized Users within that
              Org, assign roles, and access that Org's audit log and content. We are not responsible for
              disputes between an Org and its own Authorized Users.
            </li>
          </ul>

          <h2 id="license">5. Subscription License & Use Restrictions</h2>
          <p>
            Subject to your compliance with these Terms and payment of any applicable fees, we grant
            Customer a limited, non-exclusive, non-transferable, non-sublicensable right to access and
            use the Service during the applicable subscription term, solely for Customer's own internal
            government-contracting business purposes. Every Authorized User must have their own unique
            login. You agree not to:
          </p>
          <ul>
            <li>Share seats, pool credentials, or otherwise allow more individuals to use the Service than your plan's seat count permits;</li>
            <li>Access the Service through unauthenticated, automated, or bulk scraping of the API or interface, outside of features we provide for that purpose;</li>
            <li>Reverse engineer, decompile, or attempt to extract the underlying models or source code of the Service; or</li>
            <li>Resell, sublicense, or make the Service available to any third party outside your Org.</li>
          </ul>
          <p>
            <strong>Security level boundary.</strong> The Service is designed for Controlled Unclassified
            Information (CUI) and below, as described in our{" "}
            <Link to="/data-processing-addendum">Data Processing &amp; CUI Addendum</Link>, subject to{" "}
            <a href="#compliance">Section 9</a>. You may not upload, process, or store classified
            information, Top Secret information, or Special Access Program / Special Access Required
            (SAP/SAR) material through the Service under any circumstances, unless a separate,
            individually negotiated written agreement between you and us — distinct from the Data
            Processing &amp; CUI Addendum — expressly authorizes that specific use. Absent such an
            agreement, we have no obligation to support, and disclaim all liability arising from, the
            submission of such material to the Service.
          </p>

          <h2 id="plans">6. Plans, Fees & Payment</h2>
          <p>
            The Service is offered on several plans (currently Free, Team, Business, and Enterprise),
            each with its own limits on seats, monthly proposals, SAM.gov sync usage, and available
            features, as described on our <Link to="/pricing">Pricing page</Link> at the time you
            select a plan. We enforce these limits technically — for example, blocking new proposal
            creation or user invitations once a plan's limit is reached — rather than deleting or
            restricting your existing data.
          </p>
          <p>
            Paid plans are billed in advance on a recurring basis and <strong>automatically renew</strong>{" "}
            for successive terms of the same length unless you cancel before the renewal date, processed
            by a third-party payment processor. Fees are non-refundable except as required by law or as
            we expressly state otherwise. If a payment fails and isn't cured within thirty (30) days of
            our notice, we may suspend your Org's access to paid features until payment is made current.
            We may change plan pricing or features prospectively with notice; continued use after a
            price change takes effect constitutes acceptance of the new pricing for your next billing
            cycle. Upgrading or downgrading your plan may change which features and limits apply to your
            Org going forward.
          </p>

          <h2 id="content">7. Customer Data & Proposal Assets</h2>
          <p>
            As between you and us, Customer retains unconditional ownership of all Customer Data and
            Proposal Assets, including any trade secrets, pricing data, and final proposal deliverables
            contained in them. You grant us a limited license to host, process, transmit, and display
            Customer Data and Proposal Assets solely to provide and improve the Service — including
            sending relevant excerpts to AI providers as described in <a href="#ai">Section 8</a> — and
            for no other purpose.
          </p>
          <p>
            You're solely responsible for Customer Data and for having the rights necessary to upload and
            process it through the Service, including any third-party or government-furnished material.
          </p>

          <h2 id="ai">8. Generated Outputs</h2>
          <p>
            The Service uses third-party large language model providers (currently Anthropic's Claude
            models) to shred solicitation requirements, draft proposal language, and power inline editing
            assistance. To do this, relevant excerpts of Customer Data are sent to that provider for
            processing in accordance with its own terms and our{" "}
            <Link to="/privacy">Privacy Policy</Link>.
          </p>
          <p>
            Generated Outputs are produced automatically and <strong>may be inaccurate, incomplete, or
            unsuitable for submission as-is</strong>. Generated Outputs are provided for drafting
            assistance only and do not constitute professional, legal, or compliance advice. You are
            solely responsible for reviewing, verifying, and editing any Generated Output — including
            citations to your own past performance or capability data — before relying on it or
            submitting it to any government agency or Third-Party Government Portal. We do not warrant
            that Generated Outputs are accurate, compliant with any solicitation's requirements, or will
            result in a successful or compliant submission.
          </p>

          <h2 id="compliance">9. Government Compliance & Regulatory Disclaimers</h2>
          <p>
            <strong>Regulatory compliance is Customer's sole responsibility.</strong> Customer is solely
            responsible for determining and maintaining its own compliance with all applicable federal
            acquisition and technical-data-handling requirements, including the Federal Acquisition
            Regulation (FAR), Defense Federal Acquisition Regulation Supplement (DFARS), the Cybersecurity
            Maturity Model Certification (CMMC) program, NIST SP 800-171, the International Traffic in
            Arms Regulations (ITAR), and the Export Administration Regulations (EAR), with respect to any
            Controlled Unclassified Information (CUI) or export-controlled technical data that Customer
            uploads to or processes through the Service. GovCo.AI makes no representation that it is
            authorized, certified, or accredited under any such framework, and using the Service does not,
            by itself, satisfy any of Customer's regulatory obligations. See our{" "}
            <Link to="/data-processing-addendum">Data Processing &amp; CUI Addendum</Link> for our
            CUI-specific compliance posture and subprocessors.
          </p>
          <p>
            <strong>Submission disclaimer.</strong> The Service is an administrative and workflow tool.
            We do not guarantee that using the Service will result in a winning proposal, a timely upload
            to any Third-Party Government Portal, or acceptance of any submission by a government
            Contracting Officer (CO) or agency. Customer remains solely responsible for the accuracy,
            completeness, and timely submission of any proposal, through whatever channel the applicable
            solicitation requires.
          </p>

          <h2 id="samgov">10. SAM.gov & Third-Party Government Portals</h2>
          <p>
            The Service may import publicly available solicitation data from SAM.gov's Opportunities API
            to help you discover and track opportunities. This data is provided "as is" from its
            government source; we do not control its accuracy, completeness, or timeliness, and you
            should verify any solicitation's authoritative details directly on SAM.gov, or the applicable
            Third-Party Government Portal, before relying on them. We are not responsible for the
            availability, content, or terms of any Third-Party Government Portal, and your use of one is
            governed solely by that portal's own terms.
          </p>

          <h2 id="acceptable-use">11. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose or in violation of any applicable export control, sanctions, or procurement integrity law;</li>
            <li>Attempt to circumvent plan limits, seat counts, or feature gates other than by upgrading your plan;</li>
            <li>Reverse engineer, decompile, or attempt to extract the underlying models or source code of the Service;</li>
            <li>Use automated means to scrape or bulk-extract data from the Service outside of features we provide for that purpose;</li>
            <li>Upload malicious code, or content that infringes a third party's intellectual property or other rights; or</li>
            <li>Share your account credentials outside your Org, pool credentials across individuals, or use the Service to build a competing product.</li>
          </ul>
          <p>We may suspend or terminate access for violations of this section.</p>

          <h2 id="confidentiality">12. Confidentiality</h2>
          <p>
            Each party may disclose non-public, sensitive information to the other in connection with the
            Service — for Customer, this includes bidding strategies, pricing data, and Proposal Assets;
            for us, this includes non-public information about the Service ("<strong>Confidential
            Information</strong>"). Each party agrees to (a) protect the other's Confidential Information
            using at least the same degree of care it uses for its own confidential information of
            similar sensitivity, and no less than a reasonable degree of care, and (b) use it solely to
            perform its obligations or exercise its rights under these Terms. This section does not apply
            to information that is or becomes public through no fault of the receiving party, was
            already known to the receiving party without an obligation of confidentiality, or is
            independently developed without reference to the disclosing party's Confidential Information.
          </p>

          <h2 id="ip">13. Intellectual Property</h2>
          <p>
            GovCo.AI and its licensors own all right, title, and interest in the Service itself —
            including its software, algorithms, design, templates, user interface, and underlying
            technology, and any anonymized, aggregated operational metrics we derive from use of the
            Service — excluding Customer Data and Proposal Assets. Except for the limited rights
            expressly granted in these Terms, no rights are transferred to you.
          </p>

          <h2 id="security">14. Security</h2>
          <p>
            We use commercially reasonable administrative, technical, and physical safeguards designed to
            protect Customer Data. No method of transmission or storage is completely secure, and we
            cannot guarantee absolute security. See our <Link to="/privacy">Privacy Policy</Link> for
            details on how we collect, use, and protect data.
          </p>

          <h2 id="term">15. Term, Suspension & Termination</h2>
          <p>
            These Terms remain in effect while you use the Service. You may stop using the Service, or an
            Org Administrator may deactivate an Authorized User, at any time. We may suspend or terminate
            your access if you materially breach these Terms (including non-payment under{" "}
            <a href="#plans">Section 6</a>), if required by law, or if we discontinue the Service, with
            notice where reasonably practicable.
          </p>
          <p>
            Following termination of your subscription, we will make your Proposal Assets available for
            export for thirty (30) days, after which we will permanently and securely delete Customer
            Data and Proposal Assets associated with your Org, except as otherwise described in our{" "}
            <Link to="/privacy">Privacy Policy</Link> or required by law.
          </p>

          <h2 id="disclaimers">16. Disclaimers</h2>
          <p>
            THE SERVICE, INCLUDING ALL GENERATED OUTPUTS AND THIRD-PARTY DATA MADE AVAILABLE THROUGH IT,
            IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS,
            IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, NON-INFRINGEMENT, OR THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
            WE DO NOT WARRANT THAT USE OF THE SERVICE WILL RESULT IN A SUCCESSFUL, COMPLIANT, OR
            TIMELY GOVERNMENT SUBMISSION, OR THAT ANY SOLICITATION REQUIREMENT (INCLUDING FAR, DFARS,
            CMMC, OR AGENCY-SPECIFIC REQUIREMENTS) WILL BE MET BY USING THE SERVICE.
          </p>

          <h2 id="liability">17. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, GOVCO.AI AND ITS OFFICERS, EMPLOYEES, AND LICENSORS
            WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
            OR ANY LOSS OF PROFITS, REVENUE, DATA, OR A GOVERNMENT CONTRACT AWARD, ARISING OUT OF OR
            RELATED TO YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF THESE
            TERMS WILL NOT EXCEED THE TOTAL FEES CUSTOMER PAID US FOR THE SERVICE IN THE TWELVE (12)
            MONTHS BEFORE THE CLAIM AROSE.
          </p>

          <h2 id="indemnity">18. Indemnification</h2>
          <p>
            You agree to indemnify and hold GovCo.AI harmless from any claims, damages, or expenses
            (including reasonable attorneys' fees) arising from Customer Data, your breach of these
            Terms, or your violation of any law — including export control laws — or third-party right in
            connection with your use of the Service.
          </p>

          <h2 id="law">19. Governing Law, Dispute Resolution & Waivers</h2>
          <p>
            These Terms are governed by the laws of the Commonwealth of Virginia, without regard to
            conflict-of-laws principles. Any dispute arising out of these Terms will be resolved
            exclusively in the state courts located in Fairfax County, Virginia, or the U.S. District
            Court for the Eastern District of Virginia (Alexandria Division), and you consent to the
            personal jurisdiction and venue of those courts.
          </p>
          <p>
            <strong>WAIVER OF JURY TRIAL.</strong> TO THE EXTENT PERMITTED BY LAW, EACH PARTY WAIVES ANY
            RIGHT TO A TRIAL BY JURY IN ANY ACTION, PROCEEDING, OR COUNTERCLAIM ARISING OUT OF OR RELATING
            TO THESE TERMS OR THE SERVICE.
          </p>
          <p>
            <strong>WAIVER OF CLASS ACTION.</strong> ANY DISPUTE ARISING OUT OF OR RELATING TO THESE TERMS
            OR THE SERVICE MUST BE BROUGHT IN EACH PARTY'S INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR
            CLASS MEMBER IN ANY PURPORTED CLASS, COLLECTIVE, OR REPRESENTATIVE PROCEEDING.
          </p>

          <h2 id="changes">20. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. If we make material changes, we'll provide
            reasonable notice (such as an email to your account's Administrator or an in-app notice)
            before they take effect. Continued use of the Service after changes take effect constitutes
            acceptance of the updated Terms.
          </p>

          <h2 id="contact">21. Contact</h2>
          <p>
            Questions about these Terms can be sent to{" "}
            <a href="mailto:legal@govco.ai">legal@govco.ai</a>.
          </p>
        </div>
      </section>
    </div>
  );
}
